import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import { SUPABASE_CONFIGURED, supabase } from './lib/supabase';
import { AlertTriangle } from 'lucide-react';
import './App.css';

type AdminView = 'login' | 'dashboard';

function AdminApp() {
  const [currentView, setCurrentView] = useState<AdminView>('login');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const isDemo = !SUPABASE_CONFIGURED;

  // Check auth session on app load
  useEffect(() => {
    const checkAuthSession = async () => {
      if (!SUPABASE_CONFIGURED) {
        setAuthLoading(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const authenticated = !!session;
        setIsAdminAuthenticated(authenticated);
        
        // Auto-navigate to dashboard if authenticated
        if (authenticated) {
          setCurrentView('dashboard');
        }
      } catch (error) {
        console.error('Error checking auth session:', error);
        setIsAdminAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuthSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const authenticated = !!session;
      setIsAdminAuthenticated(authenticated);
      
      if (event === 'SIGNED_OUT') {
        setCurrentView('login');
      } else if (event === 'SIGNED_IN' && authenticated) {
        setCurrentView('dashboard');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const renderView = () => {
    // Show loading spinner while checking auth
    if (authLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    switch (currentView) {
      case 'login':
        // If already authenticated, redirect to dashboard
        if (isAdminAuthenticated) {
          setCurrentView('dashboard');
          return null;
        }
        return <AdminLogin onLogin={() => setCurrentView('dashboard')} />;
      
      case 'dashboard':
        // Require authentication for dashboard
        if (!isAdminAuthenticated && !isDemo) {
          setCurrentView('login');
          return null;
        }
        return <AdminDashboard onLogout={async () => {
          if (SUPABASE_CONFIGURED) {
            await supabase.auth.signOut();
          }
          setCurrentView('login');
        }} />;
      
      default:
        return <AdminLogin onLogin={() => setCurrentView('dashboard')} />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Toaster 
          position="top-right" 
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        
        {/* Demo Mode Banner */}
        {isDemo && (
          <div className="bg-orange-500 text-white px-4 py-3 text-center relative z-50">
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Admin Demo Mode Active</span>
              <span className="hidden sm:inline">- Connect Supabase for full functionality</span>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="min-h-screen">
          {renderView()}
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default AdminApp;